import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PLANS = [
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro Monthly" },
  { value: "yearly", label: "Pro Yearly" },
];

export default function AdminEntitlementModal({ open, account, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && account) {
      setForm({
        plan: account.plan || "free",
        lifetime: account.lifetime || false,
        stripe_customer_id: account.stripe_customer_id || "",
        current_period_end: account.current_period_end ? account.current_period_end.slice(0, 16) : "",
        alias_limit_override: account.alias_limit_override ?? "",
        can_use_reserved_aliases: account.can_use_reserved_aliases || false,
      });
      setReason("");
      setLoading(false);
    }
  }, [open, account]);

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setLoading(true);
    try {
      const payload = { reason: reason.trim() };

      // Only send changed fields
      if (form.plan !== (account.plan || "free")) payload.plan = form.plan;
      if (form.lifetime !== (account.lifetime || false)) payload.lifetime = form.lifetime;

      const newSid = form.stripe_customer_id.trim() || null;
      if (newSid !== (account.stripe_customer_id || null)) payload.stripe_customer_id = newSid;

      const newCpe = form.current_period_end ? new Date(form.current_period_end).toISOString() : null;
      const oldCpe = account.current_period_end || null;
      if (newCpe !== oldCpe) payload.current_period_end = newCpe;

      const newAlo = form.alias_limit_override === "" ? null : parseInt(form.alias_limit_override, 10);
      if (newAlo !== (account.alias_limit_override ?? null)) payload.alias_limit_override = isNaN(newAlo) ? null : newAlo;

      if (form.can_use_reserved_aliases !== (account.can_use_reserved_aliases || false)) {
        payload.can_use_reserved_aliases = form.can_use_reserved_aliases;
      }

      // Check if anything changed
      const hasChanges = Object.keys(payload).length > 1; // more than just "reason"
      if (!hasChanges) {
        toast("No changes to save");
        return;
      }

      const { data } = await api.patch(`/admin/pro-accounts/${account.account_id}`, payload);
      if (data.changed) {
        toast.success("Entitlements updated");
        onSaved?.();
      } else {
        toast("No changes applied");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-[480px] p-0" data-testid="entitlement-modal">
        <div className="max-h-[85vh] overflow-y-auto p-6">
          <DialogHeader>
            <div className="w-10 h-10 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-2">
              <Settings size={20} className="text-[#555555]" />
            </div>
            <DialogTitle className="text-lg">Edit Entitlements</DialogTitle>
            <DialogDescription className="text-xs text-[#888888]">
              {account.username} <span className="font-mono text-[#888888]">({account.account_id})</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Plan */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#888888]">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm(f => ({...f, plan: v}))}>
                <SelectTrigger className="h-9" data-testid="entitlement-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lifetime */}
            <div className="flex items-center gap-2.5">
              <Checkbox
                id="ent-lifetime"
                checked={form.lifetime}
                onCheckedChange={(v) => setForm(f => ({...f, lifetime: !!v, current_period_end: v ? "" : f.current_period_end}))}
                data-testid="entitlement-lifetime"
              />
              <label htmlFor="ent-lifetime" className="text-sm text-[#555555] cursor-pointer">Lifetime access</label>
            </div>

            {/* Period end */}
            {!form.lifetime && (
              <div className="space-y-1.5">
                <Label className="text-xs text-[#888888]">Current Period End</Label>
                <Input
                  type="datetime-local"
                  value={form.current_period_end}
                  onChange={(e) => setForm(f => ({...f, current_period_end: e.target.value}))}
                  className="h-9 text-sm"
                  data-testid="entitlement-period-end"
                />
              </div>
            )}

            {/* Stripe customer ID */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#888888]">Stripe Customer ID</Label>
              <Input
                value={form.stripe_customer_id}
                onChange={(e) => setForm(f => ({...f, stripe_customer_id: e.target.value}))}
                placeholder="cus_... or dev_manual"
                className="h-9 text-sm font-mono"
                data-testid="entitlement-stripe-id"
              />
              <p className="text-[10px] text-[#888888]">Leave empty for manual grants (defaults to "dev_manual")</p>
            </div>

            <div className="border-t border-[#E5E5E5] pt-4">
              <p className="text-xs font-medium text-[#555555] mb-3">Internal Privileges</p>

              {/* Alias limit override */}
              <div className="space-y-1.5 mb-3">
                <Label className="text-xs text-[#888888]">Alias Limit Override</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.alias_limit_override}
                  onChange={(e) => setForm(f => ({...f, alias_limit_override: e.target.value}))}
                  placeholder="Default (plan limit)"
                  className="h-9 text-sm"
                  data-testid="entitlement-alias-limit"
                />
                <p className="text-[10px] text-[#888888]">Overrides the plan-based alias limit. Empty = use plan default.</p>
              </div>

              {/* Reserved aliases */}
              <div className="flex items-center gap-2.5">
                <Checkbox
                  id="ent-reserved"
                  checked={form.can_use_reserved_aliases}
                  onCheckedChange={(v) => setForm(f => ({...f, can_use_reserved_aliases: !!v}))}
                  data-testid="entitlement-reserved-aliases"
                />
                <label htmlFor="ent-reserved" className="text-sm text-[#555555] cursor-pointer">
                  Can use reserved aliases <span className="text-xs text-[#888888]">(info, support, billing, contact)</span>
                </label>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-1.5 border-t border-[#E5E5E5] pt-4">
              <Label className="text-xs text-[#888888]">Reason for change *</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Internal test, support ticket #123"
                className="h-9 text-sm"
                data-testid="entitlement-reason"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1 h-10 text-xs" data-testid="entitlement-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading || !reason.trim()}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white h-10 text-xs"
                data-testid="entitlement-save"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
